import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import StickyNav from '../components/StickyNav';
import LanguageSwitch from '../components/LanguageSwitch';
import { useLanguage } from '../context/LanguageContext';
import { explorerTranslations } from '../translations/explorerTranslations';
import { WeTravelStyles, WeTravelLoadingOverlay, HoverReveal, InlinePhotoStrip } from '../components/cocon';
import { sanitizeHTML } from '../utils/sanitize';

const CoconSacre2026 = () => {
  // Language context
  const { language } = useLanguage();
  const t = (obj) => obj[language] || obj['fr'];
  const tr = explorerTranslations;

  // WeTravel modal state
  const [weTravelModal, setWeTravelModal] = useState({ isOpen: false, url: '', loading: true, showIframe: false });

  // Open WeTravel modal with loading state
  const openWeTravelModal = (url) => {
    setWeTravelModal({ isOpen: true, url, loading: true, showIframe: false });
    // Show loading message first, then show iframe after delay
    setTimeout(() => {
      setWeTravelModal(prev => ({ ...prev, showIframe: true }));
    }, 1500);
  };

  // Close WeTravel modal
  const closeWeTravelModal = () => {
    setWeTravelModal({ isOpen: false, url: '', loading: true, showIframe: false });
  };

  // Handle iframe load
  const handleIframeLoad = () => {
    setWeTravelModal(prev => ({ ...prev, loading: false }));
  };

  // SEO meta tags for main Bali page
  React.useEffect(() => {
    // Update document title - PAGE REINE SEO
    document.title = t(tr.meta.title);
    
    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", t(tr.meta.description));
    }

    // Add noindex for English version
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (language === 'en') {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = 'noindex, nofollow';
    } else if (robotsMeta) {
      robotsMeta.content = 'index, follow';
    }
    
    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://immersion-passage.preview.emergentagent.com/cocon-sacre-2026/explorer");
    }
    
    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", t(tr.meta.title));
    
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", t(tr.meta.description));
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", "https://immersion-passage.preview.emergentagent.com/cocon-sacre-2026/explorer");
  }, [language]);

  return (
    <main role="main" itemScope itemType="https://schema.org/Event">
      {/* Language Switch */}
      <LanguageSwitch />
      
      {/* Global WeTravel loading animation styles */}
      <WeTravelStyles />
      
      {/* Schema.org JSON-LD for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          "name": "COCON SACRÉ 2026 - Retraite Transformatrice à Bali",
          "description": "Retraite initiatique à Bali, Ubud et Borobudur. Une expérience signature de transformation profonde. Nage · Prie · Rayonne.",
          "startDate": "2026-05-25",
          "endDate": "2026-06-09",
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": "Bali, Ubud, Borobudur",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "ID",
              "addressRegion": "Bali"
            }
          },
          "offers": {
            "@type": "Offer",
            "name": "Arc Complet - Nage · Prie · Rayonne",
            "price": "TBD",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/LimitedAvailability",
            "url": "https://immersion-passage.preview.emergentagent.com/cocon-sacre-2026",
            "validFrom": "2025-12-01"
          },
          "organizer": {
            "@type": "Organization",
            "name": "COCON",
            "url": "https://immersion-passage.preview.emergentagent.com"
          },
          "image": "https://customer-assets.emergentagent.com/job_1ac9fdda-cac5-4642-9c93-cf04141825ca/artifacts/b8fbm0lk_%20%E2%80%A2%20Nage%20%E2%80%A2%20-2.png"
        })}
      </script>
      <div className="min-h-screen bg-[#F5F0E8]">

      <StickyNav
        links={[
          { label: language === 'fr' ? 'Pourquoi Bali\u00a0?' : 'Why Bali?', href: '#pourquoi-bali' },
          { label: language === 'fr' ? 'Arc Complet' : 'Complete Arc', href: '#arc-complet' },
          { label: language === 'fr' ? 'Nage\u00a0\u00b7\u00a0Prie' : 'Swim\u00a0\u00b7\u00a0Pray', href: '#nage-prie' },
          { label: language === 'fr' ? 'Prie\u00a0\u00b7\u00a0Rayonne' : 'Pray\u00a0\u00b7\u00a0Radiate', href: '#prie-rayonne' },
        ]}
        homeLabel="COCON SACRÉ"
        homePath="/cocon-sacre-2026"
      />

      {/* ═══════ HERO ═══════ */}
      <header className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_6bf110a9-bdc2-4301-8a33-8c9a6da0ee99/artifacts/w6mp9w3y_d46a0c86bfd9b70de7ec9add99a45379.jpg)` }}
          role="img"
          aria-label={language === 'fr' ? "Image de fond représentant Bali" : "Background image of Bali"}
        />
        {/* Stronger overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#3F2A24]/65 via-[#3F2A24]/45 to-[#3F2A24]/70" />
        
        <div className="relative z-10 text-center px-5 sm:px-6 max-w-2xl mx-auto">
          {/* 1. COCON SACRÉ - Primary title */}
          <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-[#F3E5CF] leading-tight mb-5 sm:mb-6">
            {t(tr.hero.title)}
          </h1>
          
          {/* 2. Tagline */}
          <p className="text-base sm:text-xl text-[#F3E5CF] tracking-[0.2em] sm:tracking-[0.25em] mb-4 sm:mb-5 font-light">
            {t(tr.hero.tagline)}
          </p>
          
          {/* 2.5 Premium signature phrase */}
          <p className="font-serif text-sm sm:text-lg text-[#F3E5CF]/80 italic mb-6 sm:mb-8">
            {language === 'fr' 
              ? 'Un voyage sacré à travers Bali et Java.'
              : 'A sacred journey through Bali and Java.'}
          </p>
          
          {/* 3. Location & Date - reinforced visibility */}
          <div className="mb-10 sm:mb-12 space-y-3 sm:space-y-2">
            <p className="text-xs sm:text-base tracking-[0.3em] sm:tracking-[0.35em] uppercase text-[#F3E5CF] font-medium">
              {t(tr.hero.location)}
            </p>
            <p className="text-xs sm:text-base tracking-[0.3em] sm:tracking-[0.35em] uppercase text-[#C5A059] font-semibold">
              {t(tr.hero.date)}
            </p>
          </div>
          
          {/* Primary CTA - Dominant button */}
          <a
            href="#choisis-traversee"
            onClick={(e) => { e.preventDefault(); document.querySelector('#choisis-traversee')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center justify-center px-7 sm:px-10 py-4 sm:py-5 rounded-full bg-[#C5A059] text-[#3F2A24] text-xs sm:text-base tracking-[0.15em] sm:tracking-[0.2em] uppercase font-semibold shadow-lg hover:bg-[#F3E5CF] hover:shadow-xl transition-all duration-300"
            data-testid="hero-cta-button"
          >
            {t(tr.hero.ctaButton)}
          </a>
          
          {/* Scroll indicator */}
          <div className="mt-16">
            <a
              href="#pourquoi-bali"
              className="inline-block w-px h-12 bg-[#C5A059]/60 animate-pulse"
              aria-label={language === 'fr' ? "Descendre" : "Scroll down"}
            />
          </div>
        </div>
      </header>

      {/* ═══════ TRANSITION BRIDGE ═══════ */}
      <section className="py-12 sm:py-16 px-5 sm:px-6 bg-[#F5F0E8]" data-testid="transition-bridge">
        <div className="max-w-xl mx-auto text-center space-y-5 sm:space-y-4">
          <p className="text-[#3F2A24] text-sm sm:text-lg leading-relaxed sm:leading-relaxed font-light" style={{ lineHeight: '1.9' }}>
            {t(tr.transitionBridge.line1)}
          </p>
          <p 
            className="text-[#3F2A24]/80 text-sm sm:text-lg leading-relaxed italic" 
            style={{ lineHeight: '1.9' }}
            dangerouslySetInnerHTML={sanitizeHTML(t(tr.transitionBridge.line2))}
          />
        </div>
      </section>

      {/* ═══════ MENU SQUELETTE - CHOISIS TA TRAVERSÉE ═══════ */}
      <section id="choisis-traversee" data-testid="skeleton-menu" className="bg-[#F5F0E8] pb-12 sm:pb-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          
          {/* Premium micro-heading structure */}
          <div className="text-center mb-8 sm:mb-10 space-y-3">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059]">
              {language === 'fr' ? 'Ta traversée' : 'Your journey'}
            </p>
            <h2 className="font-serif text-xl sm:text-3xl text-[#3F2A24]" style={{ textWrap: 'balance' }}>
              {t(tr.chooseSection.title)}
            </h2>
            <p className="font-serif text-sm sm:text-base text-[#3F2A24]/70 italic" style={{ textWrap: 'balance' }}>
              {t(tr.chooseSection.orientationLine)}
            </p>
          </div>
          
          {/* Three retreat cards - balanced design */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">

            {/* Arc Complet - EXPÉRIENCE SIGNATURE avec fond doré inversé */}
            <a
              href="#arc-complet"
              data-testid="skeleton-arc-complet"
              onClick={(e) => { e.preventDefault(); document.querySelector('#arc-complet')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group bg-[#C5A059]/30 border-2 border-[#C5A059]/50 rounded-2xl px-5 sm:px-6 py-8 sm:py-10 text-center space-y-4 cursor-pointer hover:bg-[#3F2A24] transition-all duration-300 shadow-sm hover:shadow-lg flex flex-col justify-between min-h-[280px] sm:min-h-[320px]"
            >
              <div>
                <p className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase font-semibold group-hover:text-[#C5A059] transition-colors duration-300">
                  {t(tr.menu.signatureExperience)}
                </p>
                <p className="font-serif text-lg sm:text-2xl text-[#3F2A24] mt-3 group-hover:text-[#F3E5CF] transition-colors duration-300">
                  {t(tr.menu.nagePrieRayonne)}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-[#3F2A24]/70 text-xs sm:text-sm tracking-wide group-hover:text-[#F3E5CF]/80 transition-colors duration-300">
                  {t(tr.menu.arcDates)}
                </p>
                <p className="text-[#3F2A24]/60 text-[10px] sm:text-xs group-hover:text-[#F3E5CF]/60 transition-colors duration-300">
                  {t(tr.menu.arcDuration)}
                </p>
              </div>
              <p className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] uppercase font-semibold group-hover:text-[#F3E5CF] group-hover:tracking-[0.38em] transition-all duration-300 pt-2">
                {t(tr.menu.fullArc)} &rarr;
              </p>
            </a>

            {/* Nage · Prie */}
            <a
              href="#nage-prie"
              data-testid="skeleton-nage-prie"
              onClick={(e) => { e.preventDefault(); document.querySelector('#nage-prie')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group bg-[#E8E0D4] rounded-2xl px-5 sm:px-6 py-8 sm:py-10 text-center space-y-4 cursor-pointer hover:bg-[#77574C] transition-all duration-300 shadow-sm hover:shadow-md flex flex-col justify-between min-h-[280px] sm:min-h-[320px]"
            >
              <div>
                <p className="text-[#3F2A24]/60 text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase group-hover:text-[#F3E5CF]/70 transition-colors duration-300">
                  {t(tr.menu.openingHeart)}
                </p>
                <p className="font-serif text-lg sm:text-2xl text-[#3F2A24] mt-3 group-hover:text-[#F3E5CF] transition-colors duration-300">
                  {t(tr.menu.nagePrie)}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-[#3F2A24]/70 text-xs sm:text-sm tracking-wide group-hover:text-[#F3E5CF]/80 transition-colors duration-300">
                  {t(tr.menu.nagePrieDates)}
                </p>
                <p className="text-[#3F2A24]/60 text-[10px] sm:text-xs group-hover:text-[#F3E5CF]/60 transition-colors duration-300">
                  {t(tr.menu.nagePrieDuration)}
                </p>
              </div>
              <p className="text-[#3F2A24]/70 text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] uppercase group-hover:text-[#C5A059] group-hover:tracking-[0.38em] transition-all duration-300 pt-2">
                {t(tr.menu.discover)} &rarr;
              </p>
            </a>

            {/* Prie · Rayonne */}
            <a
              href="#prie-rayonne"
              data-testid="skeleton-prie-rayonne"
              onClick={(e) => { e.preventDefault(); document.querySelector('#prie-rayonne')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group bg-[#E8E0D4] rounded-2xl px-5 sm:px-6 py-8 sm:py-10 text-center space-y-4 cursor-pointer hover:bg-[#77574C] transition-all duration-300 shadow-sm hover:shadow-md flex flex-col justify-between min-h-[280px] sm:min-h-[320px]"
            >
              <div>
                <p className="text-[#3F2A24]/60 text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase group-hover:text-[#F3E5CF]/70 transition-colors duration-300">
                  {t(tr.menu.heartIntegration)}
                </p>
                <p className="font-serif text-lg sm:text-2xl text-[#3F2A24] mt-3 group-hover:text-[#F3E5CF] transition-colors duration-300">
                  {t(tr.menu.prieRayonne)}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-[#3F2A24]/70 text-xs sm:text-sm tracking-wide group-hover:text-[#F3E5CF]/80 transition-colors duration-300">
                  {t(tr.menu.prieRayonneDates)}
                </p>
                <p className="text-[#3F2A24]/60 text-[10px] sm:text-xs group-hover:text-[#F3E5CF]/60 transition-colors duration-300">
                  {t(tr.menu.prieRayonneDuration)}
                </p>
              </div>
              <p className="text-[#3F2A24]/70 text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] uppercase group-hover:text-[#C5A059] group-hover:tracking-[0.38em] transition-all duration-300 pt-2">
                {t(tr.menu.discover)} &rarr;
              </p>
            </a>

          </div>
        </div>
      </section>

      {/* ═══════ POURQUOI BALI ═══════ */}
      <section id="pourquoi-bali" className="py-16 sm:py-24 px-5 sm:px-6 bg-[#9E8E7E]" data-testid="pourquoi-bali-section">
        <div className="max-w-2xl mx-auto text-center space-y-6 sm:space-y-8">
          
          {/* Premium micro-heading structure */}
          <div className="space-y-3">
            <p className="text-xs sm:text-sm tracking-[0.3em] uppercase text-[#F3E5CF] font-medium">
              {t(tr.whyBali.category)}
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#3F2A24]" style={{ textWrap: 'balance' }}>
              {t(tr.whyBali.title)}
            </h2>
            <p className="font-serif text-sm sm:text-base text-[#F3E5CF]/90 italic" style={{ textWrap: 'balance' }}>
              {t(tr.whyBali.subtitle)}
            </p>
          </div>
          
          <div className="w-16 h-[1px] bg-[#C5A059]/60 mx-auto" />
          
          <div className="space-y-5 sm:space-y-5 text-[#3F2A24]">
            <p className="text-sm sm:text-lg leading-[1.9]">{t(tr.whyBali.p1)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p2)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p3)}</p>
            <p className="text-sm sm:text-base leading-[1.9]">{t(tr.whyBali.p4)}</p>
            <p className="italic text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p5)}</p>
          </div>
          <div className="w-12 h-px bg-[#C5A059]/60 mx-auto" />
          <div className="space-y-4 sm:space-y-4 text-[#3F2A24]">
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p6)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p7)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p8)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p9)}</p>
          </div>
          <div className="w-12 h-px bg-[#C5A059]/60 mx-auto" />
          <div className="space-y-4 sm:space-y-4 text-[#3F2A24]">
            <p className="text-sm sm:text-base leading-[1.9]">{t(tr.whyBali.p10)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p11)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p12)}</p>
          </div>
          <div className="w-12 h-px bg-[#C5A059]/60 mx-auto" />
          <div className="space-y-4 sm:space-y-4 text-[#3F2A24] font-medium">
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p13)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p14)}</p>
            <p className="text-sm sm:text-base leading-[1.9]">{t(tr.whyBali.p15)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p16)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p17)}</p>
            <p className="italic text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.whyBali.p18)}</p>
          </div>
        </div>
      </section>

      {/* ═══════ POSITIONNEMENT / LIVING RITUAL ═══════ */}
      <section className="py-16 sm:py-24 px-5 sm:px-6 bg-[#E8E0D4]" data-testid="positionnement-section">
        <div className="max-w-2xl mx-auto text-center space-y-6 sm:space-y-8">
          <div className="space-y-5 text-[#3F2A24]">
            <p className="text-sm sm:text-lg leading-[1.9] whitespace-pre-line">
              {t(tr.positioning.p1)}
            </p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.positioning.p2)}</p>
            <p className="text-sm sm:text-lg leading-[1.9]">
              {t(tr.positioning.p3)}
            </p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.positioning.p4)}</p>
          </div>
          <div className="w-12 h-px bg-[#C5A059]/60 mx-auto" />
          <div className="space-y-4 text-[#5C4A3D]">
            <p className="text-sm sm:text-base leading-[1.9]">{t(tr.positioning.p5)}</p>
            <p className="text-sm sm:text-base leading-[1.9]">{t(tr.positioning.p6)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.positioning.p7)}</p>
            <p className="text-sm sm:text-base leading-[1.9] whitespace-pre-line">{t(tr.positioning.p8)}</p>
            <p className="pt-4 text-[#3F2A24] font-serif text-base sm:text-lg leading-[1.9] whitespace-pre-line">{t(tr.positioning.p9)}</p>
          </div>
          {/* Phrase signature finale */}
          <div className="pt-8">
            <div className="w-16 h-px bg-[#C5A059]/60 mx-auto mb-8" />
            <p className="text-[#C5A059] font-serif text-lg sm:text-xl italic leading-[1.9] whitespace-pre-line" style={{ textWrap: 'balance' }}>
              {t(tr.positioning.signature)}
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ TRANSITION + ARC COMPLET ═══════ */}
      <section className="py-14 sm:py-20 px-5 sm:px-6 bg-[#EDE5D5]" data-testid="label-arc-complet">
        <div className="max-w-2xl mx-auto text-center space-y-6 sm:space-y-8">
          <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#C5A059] font-medium">
            {t(tr.journey.label)}
          </p>
          <p className="font-serif text-xl sm:text-3xl text-[#3F2A24]" style={{ lineHeight: '1.6' }} dangerouslySetInnerHTML={sanitizeHTML(t(tr.journey.threeMovements))} />
          <p className="font-bold text-lg sm:text-2xl text-[#3F2A24] tracking-wide">
            {t(tr.journey.dates)}
          </p>
          <div className="py-4 sm:py-6" />
          <p className="font-serif text-2xl sm:text-4xl text-[#3F2A24] tracking-[0.08em] sm:tracking-[0.1em]">
            {t(tr.journey.nagePrieRayonne)}
          </p>
          <p className="text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#C5A059]">
            {t(tr.journey.fullArc)}
          </p>
        </div>
      </section>

      {/* ═══════ EMOTIONAL TRANSITION - PAUSE BEFORE CHOOSING ═══════ */}
      <section className="py-16 sm:py-24 px-5 sm:px-6 bg-[#E8E0D4]" data-testid="emotional-transition">
        <div className="max-w-xl mx-auto text-center space-y-6 sm:space-y-8">
          <p 
            className="text-[#3F2A24] text-sm sm:text-lg font-light"
            style={{ lineHeight: "2" }}
            dangerouslySetInnerHTML={sanitizeHTML(t(tr.emotionalTransition.p1))}
          />
          <p className="text-[#3F2A24] text-base sm:text-xl font-serif italic">
            {t(tr.emotionalTransition.p2)}
          </p>
          <div className="w-16 h-px bg-[#C5A059]/50 mx-auto my-4 sm:my-6" />
          <p className="text-[#3F2A24]/80 text-xs sm:text-base">
            {t(tr.emotionalTransition.p3)}
          </p>
        </div>
      </section>

      {/* ═══════ CHOISIS TA TRAVERSÉE ═══════ */}
      <section className="py-12 sm:py-16 px-5 sm:px-6 bg-[#D9CBAF]" data-testid="choix-intro-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-xl sm:text-3xl text-[#3F2A24] mb-3 sm:mb-4">
            {t(tr.chooseSection.title)}
          </h2>
          <p className="text-[#3F2A24]/70 text-xs sm:text-sm tracking-[0.12em] sm:tracking-[0.15em] mb-6 sm:mb-8">
            {t(tr.chooseSection.orientationLine)}
          </p>
          <p className="text-[#3F2A24]/80 text-sm sm:text-base" style={{ lineHeight: '1.8' }}>
            {language === 'fr' 
              ? <>L'arc complet est l'expérience signature.<br />Mais deux parcours cohérents existent.</> 
              : <>The complete arc is the signature experience.<br />Two coherent pathways also exist.</>}
          </p>
        </div>
      </section>

      {/* ═══════ BLOC 3 CARTES — CHOISIR SA TRAVERSÉE ═══════ */}
      <section className="py-12 sm:py-16 px-5 sm:px-6 bg-[#D9CBAF]" data-testid="choix-cartes-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8">
            
            {/* Carte 1 : ARC COMPLET */}
            <a href="#arc-complet" className="block bg-[#C5A059] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group">
              <div className="p-6 sm:p-8 space-y-5 sm:space-y-6">
                <p className="text-[10px] tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#3F2A24]/70 font-bold">
                  {t(tr.menu.signatureExperience)}
                </p>
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="font-serif text-xl sm:text-2xl text-[#3F2A24]">
                    {t(tr.menu.nagePrieRayonne)}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#3F2A24]/80">
                    {t(tr.menu.arcDates)}
                  </p>
                  <p className="text-sm sm:text-base text-[#3F2A24]/70">
                    {language === 'fr' ? '16 jours · 15 nuits' : '16 days · 15 nights'}
                  </p>
                </div>
                <div className="text-[#3F2A24]/70 text-xs sm:text-sm leading-relaxed pt-2">
                  <p>{language === 'fr' ? 'Traversée intégrale.' : 'Complete journey.'}</p>
                  <p className="pt-2">{language === 'fr' ? "L'arc complet de la transformation." : "The full arc of transformation."}</p>
                </div>
                <p className="inline-flex items-center gap-2 text-[#3F2A24] font-medium text-xs sm:text-sm tracking-[0.12em] sm:tracking-[0.15em] uppercase group-hover:gap-3 transition-all duration-300">
                  {t(tr.menu.fullArc)} →
                </p>
              </div>
            </a>

            {/* Carte 2 : NAGE · PRIE */}
            <a href="#nage-prie" className="block bg-[#E8DCCB] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group">
              <div className="p-6 sm:p-8 space-y-5 sm:space-y-6">
                <p className="text-[10px] tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#8B6A4A] font-bold">
                  {t(tr.menu.openingHeart)}
                </p>
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="font-serif text-xl sm:text-2xl text-[#3F2A24]">
                    {t(tr.menu.nagePrie)}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#3F2A24]/80">
                    {t(tr.menu.nagePrieDates)}
                  </p>
                  <p className="text-sm sm:text-base text-[#3F2A24]/70">
                    {t(tr.menu.nagePrieDuration)}
                  </p>
                </div>
                <div className="text-[#3F2A24]/70 text-xs sm:text-sm leading-relaxed pt-2">
                  <p>{language === 'fr' ? 'Ouverture.' : 'Opening.'}</p>
                  <p>{language === 'fr' ? 'Ancrage.' : 'Grounding.'}</p>
                  <p className="pt-2">{language === 'fr' ? <>Une demi-alchimie complète,<br />de l'eau au cœur.</> : <>A complete half-alchemy,<br />from water to heart.</>}</p>
                </div>
                <p className="inline-flex items-center gap-2 text-[#8B6A4A] font-medium text-xs sm:text-sm tracking-[0.12em] sm:tracking-[0.15em] uppercase group-hover:gap-3 transition-all duration-300">
                  {t(tr.menu.discover)} →
                </p>
              </div>
            </a>

            {/* Carte 3 : PRIE · RAYONNE */}
            <a href="#prie-rayonne" className="block bg-[#E8DCCB] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group">
              <div className="p-6 sm:p-8 space-y-5 sm:space-y-6">
                <p className="text-[10px] tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#8B6A4A] font-bold">
                  {t(tr.menu.heartIntegration)}
                </p>
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="font-serif text-xl sm:text-2xl text-[#3F2A24]">
                    {t(tr.menu.prieRayonne)}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#3F2A24]/80">
                    {t(tr.menu.prieRayonneDates)}
                  </p>
                  <p className="text-sm sm:text-base text-[#3F2A24]/70">
                    {language === 'fr' ? '13 jours · 12 nuits' : '13 days · 12 nights'}
                  </p>
                </div>
                <div className="text-[#3F2A24]/70 text-xs sm:text-sm leading-relaxed pt-2">
                  <p>{language === 'fr' ? 'Ancrage.' : 'Grounding.'}</p>
                  <p>{language === 'fr' ? 'Consécration.' : 'Consecration.'}</p>
                  <p className="pt-2">{language === 'fr' ? <>Du centre à l'incarnation,<br />une intégration profonde.</> : <>From center to embodiment,<br />a deep integration.</>}</p>
                </div>
                <p className="inline-flex items-center gap-2 text-[#8B6A4A] font-medium text-xs sm:text-sm tracking-[0.12em] sm:tracking-[0.15em] uppercase group-hover:gap-3 transition-all duration-300">
                  {t(tr.menu.discover)} →
                </p>
              </div>
            </a>

          </div>
        </div>
      </section>

      {/* ═══════ 3 PHOTOS — NAGE · PRIE · RAYONNE (strip défilant) + ARC COMPLET ENTRY ═══════ */}
      <section id="arc-complet" data-testid="trio-photos" className="overflow-hidden bg-[#EDE5D5]">
        {/* Title overlay for Arc Complet gallery */}
        <div className="py-6 sm:py-8 text-center bg-[#C5A059]">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#3F2A24]/60 mb-2">
            {t(tr.arcComplet.signatureExperience)}
          </p>
          <h2 className="font-serif text-2xl sm:text-4xl text-[#3F2A24]">
            {t(tr.journey.nagePrieRayonne)}
          </h2>
        </div>
        <style>{`
          @keyframes scroll-duo {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .strip-duo {
            display: flex;
            width: max-content;
            animation: scroll-duo 18s linear infinite;
          }
          .strip-duo:hover { animation-play-state: paused; }
          .strip-duo-item {
            width: 50vw;
            flex-shrink: 0;
          }
        `}</style>
        <div className="strip-duo">
          {[
            { src: "https://customer-assets.emergentagent.com/job_1ac9fdda-cac5-4642-9c93-cf04141825ca/artifacts/b8fbm0lk_%20%E2%80%A2%20Nage%20%E2%80%A2%20-2.png", alt: "Nage" },
            { src: "https://customer-assets.emergentagent.com/job_1ac9fdda-cac5-4642-9c93-cf04141825ca/artifacts/dezvykln_%E2%80%A2%20Nage%20%E2%80%A2-2.png", alt: "Prie" },
            { src: "https://customer-assets.emergentagent.com/job_1ac9fdda-cac5-4642-9c93-cf04141825ca/artifacts/blk61zm6_%E2%80%A2%20Rayonne%20%E2%80%A2%20.png", alt: "Rayonne" },
            { src: "https://customer-assets.emergentagent.com/job_1ac9fdda-cac5-4642-9c93-cf04141825ca/artifacts/b8fbm0lk_%20%E2%80%A2%20Nage%20%E2%80%A2%20-2.png", alt: "Nage" },
            { src: "https://customer-assets.emergentagent.com/job_1ac9fdda-cac5-4642-9c93-cf04141825ca/artifacts/dezvykln_%E2%80%A2%20Nage%20%E2%80%A2-2.png", alt: "Prie" },
            { src: "https://customer-assets.emergentagent.com/job_1ac9fdda-cac5-4642-9c93-cf04141825ca/artifacts/blk61zm6_%E2%80%A2%20Rayonne%20%E2%80%A2%20.png", alt: "Rayonne" },
          ].map((img, i) => (
            <div key={i} className="strip-duo-item aspect-square overflow-hidden">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ OPTION 1 — ARC COMPLET (SIGNATURE) — TEXT SECTION ═══════ */}
      <section data-testid="option-arc-complet">

        {/* --- Subtitle & Dates (compact header after gallery) --- */}
        <div className="py-8 sm:py-10 px-5 sm:px-6 text-center bg-[#C5A059]">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="font-serif text-sm sm:text-base text-[#3F2A24]/70 italic" style={{ textWrap: 'balance' }}>
              {t(tr.arcComplet.initiatory)}
            </p>
            <p className="text-xs sm:text-sm text-[#3F2A24]/60">
              {t(tr.journey.dates)}
            </p>
          </div>
        </div>

        {/* --- Lieux confirmés — BEIGE --- */}
        <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#F5F0E8]">
          <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
            <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#C5A059] text-center font-bold">
              {language === 'fr' ? 'Lieux confirmés' : 'Confirmed locations'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-6 text-center">
              <div className="space-y-2">
                <p className="font-serif text-lg sm:text-xl text-[#3F2A24]">Gili Meno &middot; Bali</p>
                <p className="text-sm sm:text-base text-[#5C4A3D]/80">{language === 'fr' ? '25.05 — 28.05.2026' : 'May 25 – May 28, 2026'}</p>
              </div>
              <div className="space-y-5">
                <div className="space-y-2">
                  <p className="font-serif text-lg sm:text-xl text-[#3F2A24]">Ubud &middot; Bali</p>
                  <p className="text-sm sm:text-base text-[#5C4A3D]/80">{language === 'fr' ? '28.05 — 02.06.2026' : 'May 28 – June 2, 2026'}</p>
                </div>
                <div className="space-y-2">
                  <p className="font-serif text-lg sm:text-xl text-[#3F2A24]">Pupuan &middot; Bali</p>
                  <p className="text-sm sm:text-base text-[#5C4A3D]/80">{language === 'fr' ? '02.06 — 05.06.2026' : 'June 2 – June 5, 2026'}</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-serif text-lg sm:text-xl text-[#3F2A24]">Borobudur &middot; Java</p>
                <p className="text-sm sm:text-base text-[#5C4A3D]/80">{language === 'fr' ? '06.06 — 09.06.2026' : 'June 6 – June 9, 2026'}</p>
              </div>
            </div>

            <div className="pt-6 sm:pt-10">
              <p className="text-center text-[#5C4A3D] text-sm sm:text-base" style={{ lineHeight: '1.8' }} dangerouslySetInnerHTML={sanitizeHTML(t(tr.arcComplet.territories))} />
            </div>
          </div>
        </div>

        {/* --- Hébergements — CRÈME FONCÉ --- */}
        <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#E8DCCB]">
          <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
            <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#C5A059] text-center font-bold">
              {t(tr.arcComplet.selectedAccommodations)}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 text-center">
              <div className="space-y-2">
                <a href="https://seriresortgilimeno.com" target="_blank" rel="noopener noreferrer" className="font-serif text-lg sm:text-xl text-[#8B6A4A] underline underline-offset-4 hover:text-[#C5A059] transition-colors block">Seri Resort &rarr;</a>
                <p className="text-sm sm:text-base text-[#5C4A3D]/80">Gili Meno</p>
              </div>
              <div className="space-y-2">
                <a href="https://www.glampingsandat.com" target="_blank" rel="noopener noreferrer" className="font-serif text-lg sm:text-xl text-[#8B6A4A] underline underline-offset-4 hover:text-[#C5A059] transition-colors block">Sandat Glamping &rarr;</a>
                <p className="text-sm sm:text-base text-[#5C4A3D]/80">Ubud</p>
              </div>
              <div className="space-y-2">
                <a href="https://dajuma.com" target="_blank" rel="noopener noreferrer" className="font-serif text-lg sm:text-xl text-[#8B6A4A] underline underline-offset-4 hover:text-[#C5A059] transition-colors block">Puri Dajuma Terrace &rarr;</a>
                <p className="text-sm sm:text-base text-[#5C4A3D]/80">Pupuan Sawah</p>
              </div>
              <div className="space-y-2">
                <a href="https://villaborobudurresort.com" target="_blank" rel="noopener noreferrer" className="font-serif text-lg sm:text-xl text-[#8B6A4A] underline underline-offset-4 hover:text-[#C5A059] transition-colors block">Villa Borobudur Resort &rarr;</a>
                <p className="text-sm sm:text-base text-[#5C4A3D]/80">Java</p>
              </div>
            </div>

            {/* External link notice */}
            <p className="text-xs text-[#5C4A3D]/50 text-center italic pt-2">
              {language === 'fr' 
                ? 'En cliquant sur le nom d\'un hôtel, vous serez redirigé vers son site officiel.' 
                : 'By clicking on a hotel name, you will be redirected to its official website.'}
            </p>
          </div>
        </div>

        {/* --- Durée — TAUPE --- */}
        <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#C5B599]">
          <div className="max-w-2xl mx-auto text-center space-y-6 sm:space-y-8">
            <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#3F2A24]/80 font-bold">
              {t(tr.arcComplet.durationLabel)}
            </p>

            <p className="font-serif text-2xl sm:text-3xl text-[#3F2A24]">
              {t(tr.arcComplet.duration)}
            </p>
            <p className="text-base sm:text-lg text-[#3F2A24]/90">
              {t(tr.journey.dates)}
            </p>

            <div className="w-12 h-px bg-[#C5A059]/50 mx-auto" />

            <div className="text-sm sm:text-base text-[#3F2A24]/80 space-y-2">
              <p>{language === 'fr' ? 'Arrivée à LOP — Lombok International Airport' : 'Arrival at LOP — Lombok International Airport'}</p>
              <p>{language === 'fr' ? 'Départ depuis YIA — Yogyakarta International Airport' : 'Departure from YIA — Yogyakarta International Airport'}</p>
            </div>

            <p className="text-[#3F2A24]/70 text-sm sm:text-base italic" style={{ lineHeight: '1.8' }}>
              {language === 'fr' 
                ? "Une arrivée anticipée dès le 23 mai est recommandée afin d'entrer dans l'expérience sans précipitation."
                : "An early arrival from May 23 is recommended to enter the experience without haste."}
            </p>
          </div>
        </div>

        {/* --- Ce que comprend — BAMBOU --- */}
        <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#8B7D5E]">
          <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">

            <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#F3E5CF] text-center font-bold">
              ✦ {t(tr.arcComplet.whatIncludes)}
            </p>

            <p className="text-center text-[#F3E5CF] text-sm sm:text-base" style={{ lineHeight: '1.8' }} dangerouslySetInnerHTML={sanitizeHTML(t(tr.arcComplet.whatIncludesIntro))} />

            <div className="w-16 h-px bg-[#C5A059]/30 mx-auto" />

            <p className="text-center text-sm sm:text-base text-[#F3E5CF]/90 tracking-wide font-medium">{t(tr.arcComplet.experienceIncludes)}</p>

            <ul className="space-y-3 sm:space-y-3 text-[#F3E5CF] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.nights)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.rooms)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.transfers)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.hotelNight)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.flight)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.javaTransfers)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.meals)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.spaces)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.ceremonies)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.priestess)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.massage)}</span></li>
              <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.includes.guidance)}</span></li>
            </ul>

            {/* ⸻ NAGE */}
            <div className="w-16 h-px bg-[#C5A059]/30 mx-auto" />

            <div className="space-y-4 sm:space-y-5">
              <p className="font-serif text-xl sm:text-2xl text-[#F3E5CF] text-center">🜄 {language === 'fr' ? 'NAGE' : 'SWIM'} &mdash; Gili Meno</p>
              <p className="text-center text-[#C5A059] text-sm sm:text-base tracking-wide font-medium">{language === 'fr' ? 'Ouverture. Lenteur. Immersion.' : 'Opening. Slowness. Immersion.'}</p>
              <ul className="space-y-3 text-[#F3E5CF] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Séjour sur une île sans voiture ni scooter' : 'Stay on an island without cars or scooters'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Hôtel en bord de mer face aux eaux turquoise' : 'Beachfront hotel facing turquoise waters'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Lever et coucher de soleil sur l\'océan' : 'Sunrise and sunset over the ocean'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Snorkeling silencieux avec les tortues' : 'Silent snorkeling with sea turtles'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Cérémonies sur la plage' : 'Ceremonies on the beach'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? "Rituels d'eau & cercles d'ouverture" : 'Water rituals & opening circles'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Marches conscientes entre mer et sable blanc' : 'Mindful walks between sea and white sand'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Massages & temps de vide réparateur' : 'Massages & restorative empty time'}</span></li>
              </ul>
              <p className="text-center text-[#F3E5CF]/80 italic text-sm sm:text-base">{language === 'fr' ? 'Une entrée progressive dans le rythme du vivant.' : 'A gradual entry into the rhythm of life.'}</p>
            </div>

            {/* ⸻ PRIE */}
            <div className="w-16 h-px bg-[#C5A059]/30 mx-auto" />

            <div className="space-y-4 sm:space-y-5">
              <p className="font-serif text-xl sm:text-2xl text-[#F3E5CF] text-center">🜃 {language === 'fr' ? 'PRIE' : 'PRAY'} &mdash; Ubud &amp; Pupuan</p>
              <p className="text-center text-[#C5A059] text-sm sm:text-base tracking-wide font-medium">{language === 'fr' ? 'Ancrage. Rituel. Terre vivante.' : 'Grounding. Ritual. Living earth.'}</p>
              <ul className="space-y-3 text-[#F3E5CF] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? '5 nuits au Sandat Glamping — piscine privée en option simple' : '5 nights at Sandat Glamping — private pool as single room option'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? '3 nuits au Puri Dajuma Terrace — rizières classées UNESCO' : '3 nights at Puri Dajuma Terrace — UNESCO-listed rice terraces'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Yoga Shala circulaire en bambou' : 'Circular bamboo Yoga Shala'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Ateliers Canang Sari & transmission balinaise' : 'Canang Sari workshops & Balinese transmission'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Cérémonie au temple de la Prêtresse Ida' : 'Ceremony at the temple of Priestess Ida'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Danse balinaise traditionnelle' : 'Traditional Balinese dance'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Sound Healing au Pyramids of Chi' : 'Sound Healing at Pyramids of Chi'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Purification aux sources & cascades sacrées' : 'Purification at sacred springs & waterfalls'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Treks contemplatifs à Pupuan' : 'Contemplative treks in Pupuan'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Cérémonies cacao & Blue Lotus' : 'Cacao & Blue Lotus ceremonies'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? "Ecstatic Dance & cercles d'intégration" : 'Ecstatic Dance & integration circles'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Temps de silence, écriture, contemplation' : 'Time for silence, writing, contemplation'}</span></li>
              </ul>
              <p className="text-center text-[#F3E5CF]/80 italic text-sm sm:text-base">{language === 'fr' ? <>Un Bali authentique.<br />Respecté.<br />Loin du tourisme de masse.</> : <>An authentic Bali.<br />Respected.<br />Far from mass tourism.</>}</p>
            </div>

            {/* ⸻ RAYONNE */}
            <div className="w-16 h-px bg-[#C5A059]/30 mx-auto" />

            <div className="space-y-4 sm:space-y-5">
              <p className="font-serif text-xl sm:text-2xl text-[#F3E5CF] text-center">🜂 {language === 'fr' ? 'RAYONNE' : 'RADIATE'} &mdash; Borobudur &amp; Java</p>
              <p className="text-center text-[#C5A059] text-sm sm:text-base tracking-wide font-medium">{language === 'fr' ? 'Intégration. Clarté. Consécration.' : 'Integration. Clarity. Consecration.'}</p>
              <ul className="space-y-3 text-[#F3E5CF] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? '3 nuits au Villa Borobudur Resort' : '3 nights at Villa Borobudur Resort'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Lever de soleil sur Borobudur (UNESCO)' : 'Sunrise over Borobudur (UNESCO)'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Visite du temple de Prambanan (UNESCO)' : 'Visit to Prambanan temple (UNESCO)'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Cérémonies de feu & rituel de clôture' : 'Fire ceremonies & closing ritual'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Cercle face au volcan Merapi' : 'Circle facing Merapi volcano'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? "Temps d'écriture & intégration consciente" : 'Writing time & conscious integration'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{language === 'fr' ? 'Silence, contemplation, massage' : 'Silence, contemplation, massage'}</span></li>
              </ul>
              <p className="text-center text-[#F3E5CF]/80 italic text-sm sm:text-base">{language === 'fr' ? 'Ici, tout se stabilise.' : 'Here, everything stabilizes.'}</p>
            </div>

            <div className="w-16 h-px bg-[#C5A059]/30 mx-auto" />

            {/* Accompagnement humain intégré */}
            <div className="space-y-4 sm:space-y-5">
              <p className="text-xs sm:text-sm tracking-[0.25em] sm:tracking-[0.3em] uppercase text-[#C5A059] text-center font-bold">
                {t(tr.arcComplet.humanGuidance)}
              </p>
              <ul className="space-y-3 text-[#F3E5CF] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.humanGuidanceItems.presence)}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.humanGuidanceItems.practitioners)}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">&bull;</span><span>{t(tr.arcComplet.humanGuidanceItems.photographer)}</span></li>
              </ul>
            </div>

            <div className="w-16 h-px bg-[#C5A059]/30 mx-auto" />

            {/* Conclusion signature */}
            <div className="text-center space-y-3 pt-4">
              <p className="font-serif text-lg sm:text-xl text-[#F3E5CF]">{language === 'fr' ? 'Une expérience intégrale.' : 'An integral experience.'}</p>
              <p className="text-[#F3E5CF]/70 text-sm sm:text-base">{language === 'fr' ? 'Rare par son accès.' : 'Rare in its access.'}</p>
              <p className="text-[#F3E5CF]/70 text-sm sm:text-base">{language === 'fr' ? 'Précise dans sa structure.' : 'Precise in its structure.'}</p>
              <p className="text-[#F3E5CF]/70 text-sm sm:text-base">{language === 'fr' ? 'Durable dans ses effets.' : 'Enduring in its effects.'}</p>
            </div>

          </div>
        </div>

        {/* --- Format & Engagement — COULEUR 1 --- */}
        <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#5C4A3D]">
          <div className="max-w-2xl mx-auto space-y-10 sm:space-y-12">

            <div className="text-center space-y-3 sm:space-y-4">
              <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#F3E5CF] font-bold">{t(tr.arcComplet.format)}</p>
              <div className="text-[#F3E5CF] space-y-2 text-sm sm:text-base">
                <p>{language === 'fr' ? 'Petit groupe confirmé.' : 'Confirmed small group.'}</p>
                <p>{language === 'fr' ? 'Accès relationnel privilégié.' : 'Privileged relational access.'}</p>
                <p>{language === 'fr' ? 'Expérience non reproductible à grande échelle.' : 'An experience that cannot be reproduced at large scale.'}</p>
              </div>
            </div>

            <div className="w-12 h-px bg-[#C5A059]/20 mx-auto" />

            <div className="text-center space-y-3 sm:space-y-4">
              <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#F3E5CF] font-bold">{t(tr.arcComplet.engagement)}</p>
              <div className="text-[#F3E5CF] space-y-3 text-sm sm:text-base">
                <p>{language === 'fr' ? 'Places limitées.' : 'Places are limited.'}</p>
                <p className="text-[#F3E5CF]/80">{language === 'fr' ? 'Toute inscription engage et valide immédiatement la réservation.' : 'Any registration confirms and immediately validates the reservation.'}</p>
              </div>
            </div>

            {/* --- Bouton WeTravel dans la section FORMAT --- */}
            <div className="pt-6 sm:pt-8">
              <div style={{ position: 'relative', textAlign: 'center' }}>
                <button 
                  onClick={() => openWeTravelModal('https://www.wetravel.com/checkout_embed?uuid=9822738838')}
                  className="wetravel-embedded-btn"
                  data-testid="wetravel-arc-complet-btn"
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#C5A059',
                    color: '#3F2A24',
                    border: '0px',
                    borderRadius: '40px',
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 500,
                    fontSize: '12px',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: '14px 34px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    minHeight: '48px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#3F2A24';
                    e.target.style.color = '#F3E5CF';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#C5A059';
                    e.target.style.color = '#3F2A24';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {t(tr.buttons.nagePrieRayonne)}
                </button>
              </div>
            </div>

          </div>
        </div>

      </section>

      {/* ═══════ STRIP PHOTOS — NAGE · PRIE (with title) ═══════ */}
      <section id="nage-prie" className="bg-[#E8E0D4]">
        {/* Title overlay for Nage · Prie gallery */}
        <div className="py-6 sm:py-8 text-center">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#5C4A3D]/60 mb-2">
            {t(tr.nagePrie.label)}
          </p>
          <h2 className="font-serif text-2xl sm:text-4xl text-[#3F2A24]">
            {t(tr.nagePrie.title)}
          </h2>
        </div>
        <InlinePhotoStrip
          bgColor="#E8E0D4"
          itemWidth="33vw"
          itemAspect="1 / 1"
          gap="0.5rem"
          padding="0.5rem"
          images={[
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/5ja4jdej_16.png', alt: 'COCON SACRÉ 2026 - Nage - Vue aérienne île Gili Meno Bali' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/d06oeln0_7.png', alt: 'COCON SACRÉ 2026 - Prie - Cérémonie spirituelle Bali encens' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/93zpj5wp_21.png', alt: 'COCON SACRÉ 2026 - Nage - Statues sous-marines cercle Gili Meno' },
            { src: 'https://customer-assets.emergentagent.com/job_dbc204f2-61f7-44da-ad8e-89635a53c438/artifacts/4t72wey1_13.png', alt: 'COCON SACRÉ 2026 - Prie - Statue gardien temple balinais offrandes fleurs frangipanier' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/hapsxqol_3.png', alt: 'COCON SACRÉ 2026 - Nage - Plage palmiers sable blanc Gili Meno' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/4kiubj3v_4.png', alt: 'COCON SACRÉ 2026 - Prie - Temple Lempuyang Gates of Heaven Bali' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/0vca7bz4_15.png', alt: 'COCON SACRÉ 2026 - Nage - Bébé tortue marine Gili Meno' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/4w2ndkq4_2.png', alt: 'COCON SACRÉ 2026 - Prie - Singe macaque temple Ubud Bali' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/t7z7a1e3_1.png', alt: 'COCON SACRÉ 2026 - Nage - Transats plage paradisiaque Gili Meno' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/lvvstgo9_14.png', alt: 'COCON SACRÉ 2026 - Prie - Cérémonie matinale rizières Bali Ubud' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/57na9crz_12.png', alt: 'COCON SACRÉ 2026 - Nage - Tortue marine eau turquoise Gili Meno' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/0mwiaur4_16.png', alt: 'COCON SACRÉ 2026 - Prie - Femme balinaise coiffe traditionnelle cérémonie' },
          ]}
        />
      </section>

      {/* ═══════ OPTION 2 — NAGE · PRIE — TEXT SECTION ═══════ */}
      <section className="bg-[#E8E0D4]" data-testid="option-nage-prie">

        {/* --- Subtitle & Dates (compact header after gallery) --- */}
        <div className="py-8 sm:py-10 px-5 sm:px-6 text-center">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="font-serif text-sm sm:text-base text-[#5C4A3D]/70 italic" style={{ textWrap: 'balance' }}>
              {t(tr.nagePrie.subtitle)}
            </p>
            <p className="text-xs sm:text-sm text-[#5C4A3D]/60">
              {t(tr.nagePrie.dates)}
            </p>
          </div>
        </div>

        <HoverReveal label={language === 'fr' ? 'Découvrir le détail' : 'Discover details'}>

          {/* --- Lieux confirmés --- */}
          <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#3F2A24]/5">
            <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
              <p className="text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D]/60 text-center">{language === 'fr' ? 'Lieux confirmés' : 'Confirmed locations'}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8 text-center">
                <div className="space-y-1">
                  <p className="font-serif text-base sm:text-lg text-[#3F2A24]">Gili Meno &middot; Bali</p>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">{language === 'fr' ? '25.05 — 28.05.2026' : 'May 25 – May 28, 2026'}</p>
                </div>
                <div className="space-y-1">
                  <p className="font-serif text-base sm:text-lg text-[#3F2A24]">Ubud &middot; Bali</p>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">{language === 'fr' ? '28.05 — 02.06.2026' : 'May 28 – June 2, 2026'}</p>
                </div>
                <div className="space-y-1">
                  <p className="font-serif text-base sm:text-lg text-[#3F2A24]">Pupuan &middot; Bali</p>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">{language === 'fr' ? '02.06 — 05.06.2026' : 'June 2 – June 5, 2026'}</p>
                </div>
              </div>
              <div className="text-center text-[#5C4A3D]/70 leading-relaxed">
                <p>{t(tr.nagePrie.territories)}</p>
                <p>{language === 'fr' ? '1 île principale & son archipel.' : '1 main island & its archipelago.'}</p>
                <p className="pt-2">{language === 'fr' ? 'Une progression fluide.' : 'A fluid progression.'}</p>
              </div>
            </div>
          </div>

          {/* --- Hébergements --- */}
          <div className="py-12 sm:py-16 px-5 sm:px-6">
            <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
              <p className="text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D]/60 text-center">{language === 'fr' ? 'Hébergements sélectionnés' : 'Selected accommodations'}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8 text-center">
                <div className="space-y-1">
                  <a href="https://seriresortgilimeno.com" target="_blank" rel="noopener noreferrer" className="font-serif text-base sm:text-lg text-[#3F2A24] hover:text-[#C5A059] transition-colors">Seri Resort</a>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">Gili Meno</p>
                </div>
                <div className="space-y-1">
                  <a href="https://www.glampingsandat.com" target="_blank" rel="noopener noreferrer" className="font-serif text-base sm:text-lg text-[#3F2A24] hover:text-[#C5A059] transition-colors">Sandat Glamping</a>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">Ubud</p>
                </div>
                <div className="space-y-1">
                  <a href="https://dajuma.com" target="_blank" rel="noopener noreferrer" className="font-serif text-base sm:text-lg text-[#3F2A24] hover:text-[#C5A059] transition-colors">Puri Dajuma Terrace</a>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">Pupuan Sawah</p>
                </div>
              </div>
              {/* External link notice */}
              <p className="text-xs text-[#5C4A3D]/40 text-center italic">
                {language === 'fr' 
                  ? 'En cliquant sur le nom d\'un hôtel, vous serez redirigé vers son site officiel.' 
                  : 'By clicking on a hotel name, you will be redirected to its official website.'}
              </p>
              <div className="text-center text-[#5C4A3D]/60 space-y-1 text-xs sm:text-sm">
                <p>Lieux intimistes.</p>
                <p>Capacit&eacute; limit&eacute;e.</p>
                <p>R&eacute;servations confirm&eacute;es pour le groupe.</p>
              </div>
            </div>
          </div>

          {/* --- Durée --- */}
          <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#3F2A24]/5">
            <div className="max-w-2xl mx-auto text-center space-y-6 sm:space-y-8">
              <p className="text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D]/60">
                {language === 'fr' ? 'Durée' : 'DURATION'}
              </p>
              <p className="font-serif text-xl sm:text-2xl text-[#3F2A24]">
                {t(tr.nagePrie.duration)}
              </p>
              <p className="text-sm sm:text-base text-[#5C4A3D]/70">
                {t(tr.nagePrie.dates)}
              </p>
              <div className="w-12 h-px bg-[#3F2A24]/15 mx-auto" />
              <div className="text-xs sm:text-sm text-[#5C4A3D]/60 space-y-2">
                <p>{language === 'fr' ? 'Arrivée à LOP — Lombok International Airport' : 'Arrival at LOP — Lombok International Airport'}</p>
                <p>{language === 'fr' ? 'Départ depuis DPS — Ngurah Rai International Airport (Bali)' : 'Departure from DPS — Ngurah Rai International Airport (Bali)'}</p>
              </div>
              <p className="text-[#5C4A3D]/50 text-xs sm:text-sm italic">
                {language === 'fr' ? 'Arrivée recommandée en Indonésie dès le 23 mai pour atterrir en douceur.' : 'Early arrival in Indonesia from May 23 is recommended to land gently.'}
              </p>
            </div>
          </div>

                    {/* --- Ce que comprend NAGE · PRIE --- */}
          <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#3F2A24]/5">
            <div className="max-w-2xl mx-auto space-y-6 sm:space-y-8">

              <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D] text-center font-bold">
                🜂 {t(tr.nagePrie.whatIncludes)}
              </p>

              <p className="text-center text-[#5C4A3D] text-sm sm:text-base" style={{ lineHeight: '1.8' }}>
                {language === 'fr' ? 'Une demi-alchimie complète.' : 'A complete half-alchemy.'}
                <br />
                {language === 'fr' ? "De l'ouverture à l'ancrage." : 'From opening to grounding.'}
              </p>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              <p className="text-center text-sm sm:text-base text-[#5C4A3D]/80">
                {language === 'fr' ? "L'expérience comprend" : 'The experience includes'}
              </p>

              <ul className="space-y-3 text-[#3F2A24] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '11 nuits dans les établissements sélectionnés' : '11 nights in selected accommodations'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Hébergement en chambre simple ou double-twin selon votre choix' : 'Single or twin-shared rooms depending on your choice'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Tous les transferts terrestres et maritimes entre les étapes' : 'All land and sea transfers between stages'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? "Une nuit d'hôtel de transition incluse" : 'One transition hotel night included'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Brunch sacré quotidien & dîners inclus' : 'Daily sacred brunch & dinners included'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Accès aux espaces privatifs et naturels' : 'Access to private and natural spaces'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '2h30 de massages balinais inclus' : '2h30 of Balinese massage included'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Cérémonies confirmées dans des cadres traditionnels préservés' : 'Ceremonies confirmed in preserved traditional settings'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Rencontre avec la Grande Prêtresse de Bali' : 'Meeting with the High Priestess of Bali'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Accompagnement structuré du premier au dernier jour' : 'Structured guidance from the first to the last day'}</span></li>
              </ul>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              {/* NAGE */}
              <div className="space-y-3 sm:space-y-4">
                <p className="font-serif text-xl sm:text-2xl text-[#3F2A24] text-center">🜄 {language === 'fr' ? 'NAGE' : 'SWIM'} — Gili Meno</p>
                <p className="text-center text-[#3F2A24] text-sm sm:text-base tracking-wide">{language === 'fr' ? 'Ouverture · Lenteur · Immersion ·' : 'Opening · Slowness · Immersion ·'}</p>
                <ul className="space-y-3 text-[#3F2A24] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Séjour sur une île sans voiture' : 'Stay on a car-free island'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Hôtel face aux eaux turquoise' : 'Hotel facing turquoise waters'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Snorkeling avec les tortues' : 'Snorkeling with sea turtles'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Cérémonies sur la plage' : 'Beach ceremonies'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? "Rituels d'eau & cercles d'ouverture" : 'Water rituals & opening circles'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Marches conscientes & intégration douce' : 'Mindful walks & gentle integration'}</span></li>
                </ul>
                <p className="text-center text-[#5C4A3D] italic text-sm sm:text-base">
                  {language === 'fr' ? <>Un seuil clair.<br />Un ralentissement profond.</> : <>A clear threshold.<br />A deep slowing down.</>}
                </p>
              </div>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              {/* PRIE */}
              <div className="space-y-3 sm:space-y-4">
                <p className="font-serif text-xl sm:text-2xl text-[#3F2A24] text-center">🜃 {language === 'fr' ? 'PRIE' : 'PRAY'} — Ubud & Pupuan</p>
                <p className="text-center text-[#3F2A24] text-sm sm:text-base tracking-wide">{language === 'fr' ? 'Ancrage · Rituel · Terre vivante ·' : 'Grounding · Ritual · Living earth ·'}</p>
                <ul className="space-y-3 text-[#3F2A24] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '5 nuits au Sandat Glamping — piscine privée en option simple' : '5 nights at Sandat Glamping — private pool as single room option'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '3 nuits au Puri Dajuma Terrace — rizières classées UNESCO' : '3 nights at Puri Dajuma Terrace — UNESCO-listed rice terraces'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Yoga Shala en bambou' : 'Bamboo Yoga Shala'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Transmission des rituels balinais' : 'Transmission of Balinese rituals'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Temple de la Prêtresse Ida' : 'Temple of Priestess Ida'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Sound Healing & danse balinaise' : 'Sound Healing & Balinese dance'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Purifications, treks contemplatifs' : 'Purifications, contemplative treks'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Cérémonies cacao & Blue Lotus' : 'Cacao & Blue Lotus ceremonies'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? "Ecstatic Dance & cercles d'intégration" : 'Ecstatic Dance & integration circles'}</span></li>
                </ul>
                <p className="text-center text-[#5C4A3D] italic text-sm sm:text-base">
                  {language === 'fr' ? <>Un Bali authentique.<br />Incarné.<br />Non touristique.</> : <>An authentic Bali.<br />Embodied.<br />Non-touristic.</>}
                </p>
              </div>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              {/* ACCOMPAGNEMENT HUMAIN */}
              <div className="space-y-4 sm:space-y-5 text-center">
                <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D] font-bold">{language === 'fr' ? 'Accompagnement humain' : 'Human guidance'}</p>
                <ul className="space-y-3 text-[#3F2A24] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Présence continue de Jeinar & des Coconeuses' : 'Continuous presence of Jeinar & the Coconeuses'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '5 femmes médecines co-facilitatrices' : '5 women-medicine co-facilitators'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Photographe & vidéographe présent·e·s tout au long du séjour' : 'Photographer & videographer present throughout the stay'}</span></li>
                </ul>
              </div>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              <p className="text-center text-[#5C4A3D] text-sm sm:text-base" style={{ lineHeight: '1.8' }}>
                {language === 'fr' ? <>Une transformation stable.<br />Structurée.<br />Incarnée.</> : <>A stable transformation.<br />Structured.<br />Embodied.</>}
              </p>

            </div>
          </div>



        </HoverReveal>

        {/* --- Bouton WeTravel NAGE · PRIE (embedded) - Bleu roi profond --- */}
        <div className="py-12 sm:py-16 px-5 sm:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <button 
                onClick={() => openWeTravelModal('https://www.wetravel.com/checkout_embed?uuid=0367256855')}
                data-testid="wetravel-nage-prie-btn"
                style={{
                  display: 'inline-block',
                  backgroundColor: '#1e3a5f',
                  color: '#F3E5CF',
                  border: '0px',
                  borderRadius: '40px',
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  fontSize: '12px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '14px 34px',
                  textDecoration: 'none',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                  minHeight: '48px'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#F3E5CF';
                  e.target.style.color = '#1e3a5f';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#1e3a5f';
                  e.target.style.color = '#F3E5CF';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {t(tr.buttons.nagePrie)}
              </button>
            </div>
          </div>
        </div>

      </section>

      {/* ═══════ STRIP PHOTOS — PRIE · RAYONNE (with title) ═══════ */}
      <section id="prie-rayonne" className="bg-[#F5F0E8]">
        {/* Title overlay for Prie · Rayonne gallery */}
        <div className="py-6 sm:py-8 text-center">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#5C4A3D]/60 mb-2">
            {t(tr.prieRayonne.label)}
          </p>
          <h2 className="font-serif text-2xl sm:text-4xl text-[#3F2A24]">
            {t(tr.prieRayonne.title)}
          </h2>
        </div>
        <InlinePhotoStrip
          bgColor="#F5F0E8"
          itemWidth="33vw"
          itemAspect="1 / 1"
          gap="0.5rem"
          padding="0.5rem"
          images={[
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/k6y084fp_5.png', alt: 'COCON SACRÉ 2026 - Prie - Offrandes eau sacrée temple Tirta Empul Bali purification' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/6z8tfpu5_2.png', alt: 'COCON SACRÉ 2026 - Rayonne - Temple Prambanan Java coucher soleil spiritualité' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/vq0x1mhs_10.png', alt: 'COCON SACRÉ 2026 - Prie - Fleurs frangipanier offrande main Bouddha Bali' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/fitv1tuy_3.png', alt: 'COCON SACRÉ 2026 - Rayonne - Volcan Mont Bromo Java lever soleil clarté' },
            { src: 'https://customer-assets.emergentagent.com/job_dbc204f2-61f7-44da-ad8e-89635a53c438/artifacts/u2zv71ik_17.png', alt: 'COCON SACRÉ 2026 - Prie - Ombrelles balinaises traditionnelles jaunes et blanches ciel bleu cérémonie' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/kaayvrm7_1.png', alt: 'COCON SACRÉ 2026 - Rayonne - Bas-reliefs Borobudur Bouddha Java sculpture bouddhiste' },
            { src: 'https://customer-assets.emergentagent.com/job_dbc204f2-61f7-44da-ad8e-89635a53c438/artifacts/qljspzy2_27.png', alt: 'COCON SACRÉ 2026 - Prie - Massage bien-être palapa toit de chaume Bali détente' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/2bwirab7_8.png', alt: 'COCON SACRÉ 2026 - Rayonne - Temple Borobudur stupas Java lumière dorée éveil' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/ap0323mm_6.png', alt: 'COCON SACRÉ 2026 - Prie - Temple Gunung Kawi fontaines sacrées mousse lotus Bali' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/kwblzkth_14.png', alt: 'COCON SACRÉ 2026 - Rayonne - Stupas Borobudur brume jungle Java panorama spirituel' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/9konrbjw_24.png', alt: 'COCON SACRÉ 2026 - Prie - Femme balinaise prière mains jointes costume traditionnel' },
            { src: 'https://customer-assets.emergentagent.com/job_871f2354-f9ec-444a-8bb2-dc0fe822eb2d/artifacts/tvmlc669_9.png', alt: 'COCON SACRÉ 2026 - Rayonne - Statue Bouddha Borobudur dos stupas Java méditation' },
          ]}
        />
      </section>

      {/* ═══════ OPTION 3 — PRIE · RAYONNE — TEXT SECTION ═══════ */}
      <section className="bg-[#F5F0E8]" data-testid="option-prie-rayonne">

        {/* --- Subtitle & Dates (compact header after gallery) --- */}
        <div className="py-8 sm:py-10 px-5 sm:px-6 text-center">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="font-serif text-sm sm:text-base text-[#5C4A3D]/70 italic" style={{ textWrap: 'balance' }}>
              {t(tr.prieRayonne.subtitle)}
            </p>
            <p className="text-xs sm:text-sm text-[#5C4A3D]/60">
              {t(tr.prieRayonne.dates)}
            </p>
          </div>
        </div>

        <HoverReveal label={language === 'fr' ? 'Découvrir le détail' : 'Discover details'}>

          {/* --- Lieux confirmés --- */}
          <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#3F2A24]/5">
            <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
              <p className="text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D]/60 text-center">{language === 'fr' ? 'Lieux confirmés' : 'Confirmed locations'}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8 text-center">
                <div className="space-y-1">
                  <p className="font-serif text-base sm:text-lg text-[#3F2A24]">Ubud &middot; Bali</p>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">{language === 'fr' ? '28.05 — 02.06.2026' : 'May 28 – June 2, 2026'}</p>
                </div>
                <div className="space-y-1">
                  <p className="font-serif text-base sm:text-lg text-[#3F2A24]">Pupuan &middot; Bali</p>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">{language === 'fr' ? '02.06 — 05.06.2026' : 'June 2 – June 5, 2026'}</p>
                </div>
                <div className="space-y-1">
                  <p className="font-serif text-base sm:text-lg text-[#3F2A24]">Borobudur &middot; Java</p>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">{language === 'fr' ? '06.06 — 09.06.2026' : 'June 6 – June 9, 2026'}</p>
                </div>
              </div>
              <div className="text-center text-[#5C4A3D]/70 leading-relaxed">
                <p>{language === 'fr' ? '3 territoires.' : '3 territories.'}</p>
                <p>{language === 'fr' ? '2 îles principales.' : '2 main islands.'}</p>
                <p className="pt-2">{language === 'fr' ? 'Du cœur à la consécration.' : 'From heart to consecration.'}</p>
              </div>
            </div>
          </div>

          {/* --- Hébergements --- */}
          <div className="py-12 sm:py-16 px-5 sm:px-6">
            <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
              <p className="text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D]/60 text-center">{language === 'fr' ? 'Hébergements sélectionnés' : 'Selected accommodations'}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8 text-center">
                <div className="space-y-1">
                  <a href="https://www.glampingsandat.com" target="_blank" rel="noopener noreferrer" className="font-serif text-base sm:text-lg text-[#3F2A24] hover:text-[#C5A059] transition-colors">Sandat Glamping</a>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">Ubud</p>
                </div>
                <div className="space-y-1">
                  <a href="https://dajuma.com" target="_blank" rel="noopener noreferrer" className="font-serif text-base sm:text-lg text-[#3F2A24] hover:text-[#C5A059] transition-colors">Puri Dajuma Terrace</a>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">Pupuan Sawah</p>
                </div>
                <div className="space-y-1">
                  <a href="https://villaborobudurresort.com" target="_blank" rel="noopener noreferrer" className="font-serif text-base sm:text-lg text-[#3F2A24] hover:text-[#C5A059] transition-colors">Villa Borobudur Resort</a>
                  <p className="text-xs sm:text-sm text-[#5C4A3D]/50">Java</p>
                </div>
              </div>
              {/* External link notice */}
              <p className="text-xs text-[#5C4A3D]/40 text-center italic">
                {language === 'fr' 
                  ? 'En cliquant sur le nom d\'un hôtel, vous serez redirigé vers son site officiel.' 
                  : 'By clicking on a hotel name, you will be redirected to its official website.'}
              </p>
              <div className="text-center text-[#5C4A3D]/60 space-y-1 text-xs sm:text-sm">
                <p>{language === 'fr' ? 'Lieux intimistes.' : 'Intimate settings.'}</p>
                <p>{language === 'fr' ? 'Capacité limitée.' : 'Limited capacity.'}</p>
                <p>{language === 'fr' ? 'Réservations confirmées pour le groupe.' : 'Reservations confirmed for the group.'}</p>
              </div>
            </div>
          </div>

          {/* --- Durée --- */}
          <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#3F2A24]/5">
            <div className="max-w-2xl mx-auto text-center space-y-6 sm:space-y-8">
              <p className="text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D]/60">
                {language === 'fr' ? 'Durée' : 'DURATION'}
              </p>
              <p className="font-serif text-xl sm:text-2xl text-[#3F2A24]">
                {t(tr.prieRayonne.duration)}
              </p>
              <p className="text-sm sm:text-base text-[#5C4A3D]/70">
                {t(tr.prieRayonne.dates)}
              </p>
              <div className="w-12 h-px bg-[#3F2A24]/15 mx-auto" />
              <div className="text-xs sm:text-sm text-[#5C4A3D]/60 space-y-2">
                <p>{language === 'fr' ? 'Arrivée à DPS — Ngurah Rai International Airport (Bali)' : 'Arrival at DPS — Ngurah Rai International Airport (Bali)'}</p>
                <p>{language === 'fr' ? 'Départ depuis YIA — Yogyakarta International Airport' : 'Departure from YIA — Yogyakarta International Airport'}</p>
              </div>
              <p className="text-[#5C4A3D]/50 text-xs sm:text-sm italic">
                {language === 'fr' ? 'Arrivée recommandée à Bali dès le 26 mai pour atterrir en douceur.' : 'Early arrival in Bali from May 26 is recommended to land gently.'}
              </p>
            </div>
          </div>

                    {/* --- Ce que comprend PRIE · RAYONNE --- */}
          <div className="py-12 sm:py-16 px-5 sm:px-6 bg-[#3F2A24]/5">
            <div className="max-w-2xl mx-auto space-y-6 sm:space-y-8">

              <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D] text-center font-bold">
                🜂 {t(tr.prieRayonne.whatIncludes)}
              </p>

              <p className="text-center text-[#5C4A3D] text-sm sm:text-base" style={{ lineHeight: '1.8' }}>
                {language === 'fr' ? <>Du cœur à la consécration.<br />Un ancrage suivi d'une intégration.</> : <>From heart to consecration.<br />Grounding followed by integration.</>}
              </p>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              <p className="text-center text-sm sm:text-base text-[#5C4A3D]/80">{language === 'fr' ? "L'expérience comprend" : 'The experience includes'}</p>

              <ul className="space-y-3 text-[#3F2A24] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '11 nuits dans les établissements sélectionnés' : '11 nights in selected accommodations'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Hébergement en chambre simple ou double-twin selon votre choix' : 'Single or twin-shared rooms depending on your choice'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Tous les transferts terrestres' : 'All land transfers'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Vol interne Bali → Java inclus' : 'Internal Bali → Java flight included'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Tous les transferts à Java pris en charge' : 'All Java transfers covered'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Brunch sacré quotidien & dîners inclus' : 'Daily sacred brunch & dinners included'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Accès aux espaces privatifs et naturels' : 'Access to private and natural spaces'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '2h30 de massages balinais inclus' : '2h30 of Balinese massage included'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Cérémonies confirmées dans des cadres traditionnels préservés' : 'Ceremonies confirmed in preserved traditional settings'}</span></li>
                <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Accompagnement structuré du premier au dernier jour' : 'Structured guidance from the first to the last day'}</span></li>
              </ul>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              {/* PRIE */}
              <div className="space-y-3 sm:space-y-4">
                <p className="font-serif text-xl sm:text-2xl text-[#3F2A24] text-center">🜃 {language === 'fr' ? 'PRIE' : 'PRAY'} — Ubud & Pupuan</p>
                <p className="text-center text-[#3F2A24] text-sm sm:text-base tracking-wide">{language === 'fr' ? 'Ancrage · Rituel · Terre vivante ·' : 'Grounding · Ritual · Living earth ·'}</p>
                <ul className="space-y-3 text-[#3F2A24] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '5 nuits au Sandat Glamping — piscine privée en option simple' : '5 nights at Sandat Glamping — private pool as single room option'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '3 nuits au Puri Dajuma Terrace' : '3 nights at Puri Dajuma Terrace'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Yoga Shala en bambou' : 'Bamboo Yoga Shala'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Transmission balinaise' : 'Balinese transmission'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Temple de la Prêtresse Ida' : 'Temple of Priestess Ida'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Purifications & rituels' : 'Purifications & rituals'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Treks dans les rizières classées UNESCO' : 'Treks in UNESCO-listed rice terraces'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Sound Healing & Ecstatic Dance' : 'Sound Healing & Ecstatic Dance'}</span></li>
                </ul>
                <p className="text-center text-[#5C4A3D] italic text-sm sm:text-base">
                  {language === 'fr' ? <>Un ancrage réel.<br />Profond.</> : <>A real grounding.<br />Deep.</>}
                </p>
              </div>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              {/* RAYONNE */}
              <div className="space-y-3 sm:space-y-4">
                <p className="font-serif text-xl sm:text-2xl text-[#3F2A24] text-center">🜂 {language === 'fr' ? 'RAYONNE' : 'RADIATE'} — Borobudur & Java</p>
                <p className="text-center text-[#3F2A24] text-sm sm:text-base tracking-wide">{language === 'fr' ? 'Intégration · Clarté · Consécration ·' : 'Integration · Clarity · Consecration ·'}</p>
                <ul className="space-y-3 text-[#3F2A24] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? '3 nuits au Villa Borobudur Resort' : '3 nights at Villa Borobudur Resort'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Lever de soleil sur Borobudur (UNESCO)' : 'Sunrise over Borobudur (UNESCO)'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Visite de Prambanan (UNESCO)' : 'Visit to Prambanan (UNESCO)'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Cérémonies de feu & rituel de clôture' : 'Fire ceremonies & closing ritual'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Cercle face au volcan Merapi' : 'Circle facing Merapi volcano'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? "Temps d'écriture & intégration" : 'Writing time & integration'}</span></li>
                </ul>
                <p className="text-center text-[#5C4A3D] italic text-sm sm:text-base">
                  {language === 'fr' ? <>Une stabilisation intérieure.<br />Un départ aligné.</> : <>An inner stabilization.<br />An aligned departure.</>}
                </p>
              </div>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              {/* ACCOMPAGNEMENT HUMAIN */}
              <div className="space-y-4 sm:space-y-5 text-center">
                <p className="text-xs sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#5C4A3D] font-bold">{language === 'fr' ? 'Accompagnement humain' : 'Human guidance'}</p>
                <ul className="space-y-3 text-[#3F2A24] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Présence continue de Jeinar & des Coconeuses' : 'Continuous presence of Jeinar & the Coconeuses'}</span></li>
                  <li className="flex gap-3"><span className="text-[#C5A059] shrink-0">•</span><span>{language === 'fr' ? 'Photographe & vidéographe professionnels' : 'Professional photographer & videographer'}</span></li>
                </ul>
              </div>

              <div className="w-12 h-px bg-[#3F2A24]/20 mx-auto" />

              <p className="text-center text-[#5C4A3D] text-sm sm:text-base" style={{ lineHeight: '1.8' }}>
                {language === 'fr' ? <>Une transformation claire.<br />Intégrée.<br />Durable.</> : <>A clear transformation.<br />Integrated.<br />Enduring.</>}
              </p>

            </div>
          </div>



        </HoverReveal>

        {/* --- Bouton WeTravel PRIE · RAYONNE (embedded) - Orange sacré --- */}
        <div className="py-12 sm:py-16 px-5 sm:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <button 
                onClick={() => openWeTravelModal('https://www.wetravel.com/checkout_embed?uuid=4500382141')}
                data-testid="wetravel-prie-rayonne-btn"
                style={{
                  display: 'inline-block',
                  backgroundColor: '#b86b3a',
                  color: '#F3E5CF',
                  border: '0px',
                  borderRadius: '40px',
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  fontSize: '12px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '14px 34px',
                  textDecoration: 'none',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                  minHeight: '48px'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#F3E5CF';
                  e.target.style.color = '#b86b3a';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#b86b3a';
                  e.target.style.color = '#F3E5CF';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {t(tr.buttons.prieRayonne)}
              </button>
            </div>
          </div>
        </div>

      </section>

      {/* ═══════ AVANT DE RÉSERVER - REASSURANCE BLOCK ═══════ */}
      <section className="py-16 sm:py-24 px-5 sm:px-6 bg-[#F5F0E8]" data-testid="before-you-book">

        <div className="max-w-xl mx-auto text-center space-y-6 sm:space-y-8">
          <h3 className="font-serif text-xl sm:text-2xl text-[#3F2A24]">
            {t(tr.beforeYouBook.title)}
          </h3>
          <div className="w-12 h-px bg-[#C5A059]/50 mx-auto" />
          <div className="space-y-5 sm:space-y-6 text-[#3F2A24]">
            <p className="text-sm sm:text-base leading-[1.9]">
              {t(tr.beforeYouBook.p1)}
            </p>
            <p className="text-sm sm:text-base leading-[1.9]">
              {t(tr.beforeYouBook.p2)}
            </p>
            <p className="text-sm sm:text-base leading-[1.9]">
              {t(tr.beforeYouBook.p3)}
            </p>
            <p className="text-sm sm:text-base leading-[1.9] italic text-[#5C4A3D]">
              {t(tr.beforeYouBook.p4)}
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ ÉDITIONS PRÉCÉDENTES ═══════ */}
      <section className="py-10 sm:py-12 px-5 sm:px-6 bg-[#E8D2B5]/60">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.35em] uppercase font-medium text-[#3F2A24] mb-6 sm:mb-8">
            {t(tr.previousEditions.title)}
          </h3>
          
          {/* COCON SACRÉ previous editions ONLY */}
          <div className="max-w-md mx-auto">
            {/* COCON Sacré 2025 Bali */}
            <Link
              to="/cocons-2025/bali"
              className="group block p-5 sm:p-6 rounded-xl bg-[#F5EDE4] border border-[#8B7B6B]/30 hover:bg-[#F3E5CF] transition-all duration-300"
            >
              <span className="block text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase font-medium text-[#5C4A3D] mb-2">
                COCON Sacré 2025
              </span>
              <span className="block text-[0.6rem] sm:text-[0.65rem] tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[#8B7B6B] mb-2 sm:mb-3">
                Bali · {language === 'fr' ? 'Mars' : 'March'} 2025
              </span>
              <span className="block text-xs sm:text-sm text-[#6B5A4D] italic mb-3 sm:mb-4">
                {language === 'fr' ? 'Photos & Témoignages' : 'Photos & Testimonials'}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.15em] uppercase text-[#5C4A3D] group-hover:text-[#3F2A24]">
                {language === 'fr' ? 'Découvrir' : 'Discover'} →
              </span>
            </Link>
          </div>

          {/* Link to discover other retreats */}
          <div className="mt-8 pt-6 border-t border-[#8B7B6B]/20">
            <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#8B7B6B] mb-4">
              {language === 'fr' ? 'Découvrir aussi' : 'Also discover'}
            </p>
            <Link
              to="/valouse"
              className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#5C4A3D] hover:text-[#C5A059] transition-colors"
            >
              COCON VALOUSE 2026 →
            </Link>
            {/* ═══ CONSERVATION TEMPORAIRE — ancien lien inactif + tooltip ═══
            <div className="relative inline-block group cursor-default">
              <span className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#5C4A3D]/40">
                COCON VALOUSE 2026
              </span>
              <div className="absolute left-0 top-full mt-2 px-4 py-2 bg-[#3F2A24] text-[#F3E5CF] text-xs rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                Cette page sera disponible dès le 18 mars 2026
              </div>
            </div>
            ═══ FIN CONSERVATION TEMPORAIRE ═══ */}
          </div>
        </div>
      </section>

      {/* ═══════ RETOUR ═══════ */}
      <section className="py-10 sm:py-12 px-5 sm:px-6 bg-[#F5F0E8]">
        <div className="max-w-2xl mx-auto text-center">
          <Link
            to="/cocon-sacre-2026"
            className="inline-flex items-center gap-2 text-[#5C4A3D]/60 text-xs sm:text-sm hover:text-[#3F2A24] transition-colors min-h-[44px]"
            data-testid="back-to-cocon-sacre"
          >
            <ArrowLeft className="w-4 h-4" />
            {t(tr.back)}
          </Link>
        </div>
      </section>

      </div>

      {/* WeTravel Embedded Modal */}
      {weTravelModal.isOpen && (
        <div 
          className="wetravel-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeWeTravelModal();
          }}
        >
          {/* Loading Message */}
          {weTravelModal.loading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: '#F3E5CF',
              zIndex: 10001
            }}>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '28px',
                fontWeight: 500,
                letterSpacing: '0.1em',
                marginBottom: '20px'
              }}>{t(tr.loading.title)}</div>
              <div style={{
                width: '60px',
                height: '2px',
                background: '#C5A059',
                margin: '0 auto',
                animation: 'wtPulse 1.5s ease-in-out infinite'
              }}></div>
            </div>
          )}

          {/* Modal Content */}
          {weTravelModal.showIframe && (
            <div className="wetravel-modal-content">
              <button 
                className="wetravel-modal-close"
                onClick={closeWeTravelModal}
                aria-label={language === 'fr' ? 'Fermer' : 'Close'}
              >
                ✕
              </button>
              <iframe
                src={weTravelModal.url}
                className="wetravel-modal-iframe"
                title={language === 'fr' ? 'Réservation WeTravel' : 'WeTravel Booking'}
                onLoad={handleIframeLoad}
              />
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default CoconSacre2026;
